import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { StatusBadge } from "@cx/ui";
import type { ShipmentList } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { ShipmentDialog } from "./LogisticsDialogs";
import { SHIPMENT_STATUS_TONE, statusLabel } from "./labels";

/** Painel "Envios" do pedido de venda (T3.4): objetos do pedido e "Gerar etiqueta" (cotação → objeto). */
export function ShipmentsPanel({
  salesOrderId,
  canceled,
}: {
  salesOrderId: string;
  canceled: boolean;
}): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: ["logistics", companyId, "shipments", "order", salesOrderId],
    queryFn: () => api<ShipmentList>(`/logistics/shipments?salesOrderId=${encodeURIComponent(salesOrderId)}`),
  });
  const rows = list.data?.data ?? [];
  return (
    <div className="box card p-3 mb-3">
      <h2 className="h6">Envios</h2>
      {list.isSuccess && rows.length === 0 && (
        <p className="text-muted small mb-2">Nenhum objeto de postagem.</p>
      )}
      {rows.length > 0 && (
        <ul className="list-unstyled small mb-2" aria-label="Objetos do pedido">
          {rows.map((s) => (
            <li key={s.id} className="d-flex justify-content-between align-items-center gap-2 mb-1">
              <span>
                <Link to="/vendas/logistica/$id" params={{ id: s.id }}>
                  {s.isReverse ? "Reversa" : "Objeto"} {s.number}
                </Link>
                {s.trackingCode && (
                  <span className="d-block font-monospace text-muted">{s.trackingCode}</span>
                )}
              </span>
              <StatusBadge tone={SHIPMENT_STATUS_TONE[s.status]}>
                {statusLabel(s.status, s.isReverse)}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
      {can(perms, "logistics", "create") && !canceled && (
        <div className="d-grid">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setOpen(true)}>
            Gerar etiqueta
          </button>
        </div>
      )}
      <ShipmentDialog
        open={open}
        salesOrderId={salesOrderId}
        onClose={() => setOpen(false)}
        onDone={(s) => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ["logistics", companyId] });
          void navigate({ to: "/vendas/logistica/$id", params: { id: s.id } });
        }}
      />
    </div>
  );
}
